import React, { useEffect, useState } from 'react';
import {
	Dialog,
	DialogContent,
	IconButton,
	Box,
	CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { FaCoins, FaFutbol, FaBullseye, FaExchangeAlt } from 'react-icons/fa';
import matchService from '../services/matchService';
import { translateTeamName } from '../utils/roundTranslator';
import '../styles/MatchOddsModal.css';

const formatOdd = (v) => (v == null ? '–' : Number(v).toFixed(2));

const formatFetchedAt = (iso, locale) => {
	if (!iso) return '';
	try {
		const d = new Date(iso);
		return d.toLocaleString(locale || undefined, {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		});
	} catch {
		return '';
	}
};

const MatchOddsModal = ({ open, matchId, homeTeam, awayTeam, onClose }) => {
	const { t, i18n } = useTranslation();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [data, setData] = useState(null);

	useEffect(() => {
		if (!open || !matchId) return;
		let cancelled = false;
		setLoading(true);
		setError(null);
		setData(null);

		matchService.getMatchOdds(matchId)
			.then((res) => {
				if (cancelled) return;
				if (!res) {
					setError(t('oddsModal.noData', { defaultValue: 'Kurzy pro tento zápas nejsou k dispozici.' }));
				} else {
					setData(res);
				}
			})
			.catch((err) => {
				if (cancelled) return;
				const status = err?.response?.status;
				if (status === 404 || status === 204) {
					setError(t('oddsModal.noData', { defaultValue: 'Kurzy pro tento zápas nejsou k dispozici.' }));
				} else {
					setError(t('oddsModal.loadFailed', { defaultValue: 'Nepodařilo se načíst kurzy.' }));
				}
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [open, matchId, t]);

	const homeName = translateTeamName(homeTeam?.name || '', t);
	const awayName = translateTeamName(awayTeam?.name || '', t);

	const renderMatchWinner = () => {
		const mw = data?.matchWinner;
		if (!mw) return null;
		return (
			<div className="om-card">
				<div className="om-card-title">
					<FaFutbol /> {t('oddsModal.matchWinner', { defaultValue: 'Vítěz zápasu' })}
				</div>
				<div className="om-row om-row-3">
					<div className="om-cell">
						<div className="om-cell-label">{homeName || t('oddsModal.home', { defaultValue: 'Domácí' })}</div>
						<div className="om-cell-odd om-home">{formatOdd(mw.home)}</div>
					</div>
					<div className="om-cell">
						<div className="om-cell-label">{t('oddsModal.draw', { defaultValue: 'Remíza' })}</div>
						<div className="om-cell-odd om-draw">{formatOdd(mw.draw)}</div>
					</div>
					<div className="om-cell">
						<div className="om-cell-label">{awayName || t('oddsModal.away', { defaultValue: 'Hosté' })}</div>
						<div className="om-cell-odd om-away">{formatOdd(mw.away)}</div>
					</div>
				</div>
			</div>
		);
	};

	const renderOverUnder = () => {
		const list = data?.overUnder;
		if (!list || list.length === 0) return null;
		return (
			<div className="om-card">
				<div className="om-card-title">
					<FaExchangeAlt /> {t('oddsModal.overUnder', { defaultValue: 'Více / méně gólů' })}
				</div>
				<div className="om-ou-grid">
					<div className="om-ou-head">{t('oddsModal.line', { defaultValue: 'Hranice' })}</div>
					<div className="om-ou-head">{t('oddsModal.over', { defaultValue: 'Více' })}</div>
					<div className="om-ou-head">{t('oddsModal.under', { defaultValue: 'Méně' })}</div>
					{list.map((row) => (
						<React.Fragment key={row.line}>
							<div className="om-ou-line">{row.line.toFixed(1)}</div>
							<div className="om-ou-odd">{formatOdd(row.over)}</div>
							<div className="om-ou-odd">{formatOdd(row.under)}</div>
						</React.Fragment>
					))}
				</div>
			</div>
		);
	};

	const renderBtts = () => {
		const btts = data?.bothTeamsScore;
		if (!btts) return null;
		return (
			<div className="om-card">
				<div className="om-card-title">
					<FaCoins /> {t('oddsModal.btts', { defaultValue: 'Oba týmy skórují' })}
				</div>
				<div className="om-row om-row-2">
					<div className="om-cell">
						<div className="om-cell-label">{t('oddsModal.yes', { defaultValue: 'Ano' })}</div>
						<div className="om-cell-odd om-home">{formatOdd(btts.yes)}</div>
					</div>
					<div className="om-cell">
						<div className="om-cell-label">{t('oddsModal.no', { defaultValue: 'Ne' })}</div>
						<div className="om-cell-odd om-away">{formatOdd(btts.no)}</div>
					</div>
				</div>
			</div>
		);
	};

	const renderExactScore = () => {
		const list = data?.exactScore;
		if (!list || list.length === 0) return null;
		const top = list.slice(0, 12);
		return (
			<div className="om-card">
				<div className="om-card-title">
					<FaBullseye /> {t('oddsModal.exactScore', { defaultValue: 'Přesný výsledek' })}
					<span className="om-card-sub">
						({t('oddsModal.topByOdd', { defaultValue: 'nejpravděpodobnější' })})
					</span>
				</div>
				<div className="om-es-grid">
					{top.map((row) => (
						<div key={row.score} className="om-es-cell">
							<div className="om-es-score">{row.score.replace(':', ' : ')}</div>
							<div className="om-es-odd">{formatOdd(row.odd)}</div>
						</div>
					))}
				</div>
			</div>
		);
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			PaperProps={{ className: 'om-paper' }}
			sx={{
				'& .MuiBackdrop-root': {
					backgroundColor: 'rgba(0, 0, 0, 0.6)',
					backdropFilter: 'blur(3px)',
				},
			}}
		>
			<IconButton onClick={onClose} className="om-close" aria-label="close">
				<CloseIcon />
			</IconButton>
			<DialogContent className="om-content">
				{loading && (
					<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
						<CircularProgress />
					</Box>
				)}
				{!loading && error && (
					<div className="om-error">{String(error)}</div>
				)}
				{!loading && !error && data && (
					<>
						<div className="om-hero">
							<div className="om-hero-title">
								{t('oddsModal.title', { defaultValue: 'Sázkové kurzy' })}
							</div>
							<div className="om-hero-match">
								<span className="om-team">{homeName}</span>
								<span className="om-vs">vs</span>
								<span className="om-team">{awayName}</span>
							</div>
							<div className="om-hero-meta">
								{data.bookmakerName && (
									<span className="om-meta-pill">{data.bookmakerName}</span>
								)}
								{data.fetchedAt && (
									<span className="om-meta-date">
										{t('oddsModal.updated', { defaultValue: 'Aktualizováno' })}: {formatFetchedAt(data.fetchedAt, i18n.language)}
									</span>
								)}
							</div>
						</div>

						<div className="om-grid">
							{renderMatchWinner()}
							{renderBtts()}
							{renderOverUnder()}
							{renderExactScore()}
						</div>

						<div className="om-disclaimer">
							{t('oddsModal.disclaimer', {
								defaultValue: 'Kurzy jsou pouze orientační, slouží k analýze a nepředstavují sázkovou nabídku.'
							})}
						</div>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
};

export default MatchOddsModal;
